"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { ArrowLeft, Save, Loader2, MessageSquare, Lightbulb } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "../../../lib/textron-data-processor";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Textarea } from "../../ui/textarea";

interface ManagementUseOfExpertAssessmentProps {
  onBack?: () => void;
}

interface ExpertColumn {
  id: string;
  label: string;
}

interface QuestionData {
  [expertId: string]: string; // Just store plain text response
}

interface Question {
  question_id: string;
  question_text: string;
  reference_literature: string;
}

interface Section {
  heading: string;
  heading_text: string;
  questions: Question[];
}

const TEMPLATE_DATA = {
  "template_name": "Use of Management Expert's Work",
  "structure": [
    {
      "heading": "A",
      "heading_text": "Evaluate the competence, capability and objectivity of the expert",
      "questions": [
        {
          "question_id": "A1",
          "question_text": "1. Document our understanding of the professional qualifications of the management's expert in the particular field and the entity that employs the expert and assess the level of competence and capabilities of the expert in the particular field",
          "reference_literature": "Factors that are relevant to the assessment of expert's competence include:\n(i) Personal experience with previous work of that expert\n(ii) Discussions with that expert.\n(iii) Discussions with others who are familiar with that expert's work\n(iv) Knowledge of that expert's qualifications, membership of a professional body or industry association, license to practice, or other forms of external recognition\n(v) Published papers or books written by that expert.\n(vi) An auditor's expert, if any, who assists the auditor in obtaining sufficient appropriate audit evidence with respect to information produced by the management's expert."
        },
        {
          "question_id": "A2",
          "question_text": "Additionally, document our consideration of the below matters that may be relevant:",
          "reference_literature": "(i) The relevance of the management's expert's competence to the matter for which that expert's work will be used, including any areas of specialty within that expert's field. For example, a particular actuary may specialise in property and casualty insurance, but have limited expertise regarding pension calculations.\n(ii) The management's expert's competence with respect to relevant accounting requirements, for example, knowledge of assumptions and methods, including models where applicable, that are consistent with the applicable financial reporting framework.\n(iii) Whether unexpected events, changes in conditions, or the audit evidence obtained from the results of audit procedures indicate that it may be necessary to reconsider the initial evaluation of the competence, capabilities and objectivity of the management's expert as the audit progresses."
        },
        {
          "question_id": "A3",
          "question_text": "Document our assessment of any circumstances that may threaten the objectivity of the expert",
          "reference_literature": "A broad range of circumstances may threaten objectivity, for example, self-interest threats, advocacy threats, familiarity threats, self-review threats and intimidation threats."
        },
        {
          "question_id": "A4",
          "question_text": "When evaluating the objectivity of an expert engaged by the entity, it may be relevant to discuss with management and that expert any interests and relationships that may create threats to the expert's objectivity, and any applicable safeguards, including any professional requirements that apply to the expert; and to evaluate whether the safeguards are adequate",
          "reference_literature": "Document conclusions reached based on discussions with management and the expert any interests and relationships and any applicable safeguards.\nInterest and relationships creating threats may include:\n(a) Financial interests;\n(b) Personal and business relationships; and\n(c) Provision of services"
        }
      ]
    },
    {
      "heading": "B",
      "heading_text": "Obtain an understanding of the work of management's expert",
      "questions": [
        {
          "question_id": "B1",
          "question_text": "Document our understanding of the relevant field of expertise of management's expert including:",
          "reference_literature": "(i) Whether that expert's field has areas of specialty within it that are relevant to the audit.\n(ii) Whether any professional or other standards, and regulatory or legal requirements apply.\n(iii) What assumptions and methods are used by the management's expert, and whether they are generally accepted within that expert's field and appropriate for financial reporting purposes.\n(iv) The nature of internal and external data or information the auditor's expert uses."
        },
        {
          "question_id": "B2",
          "question_text": "Document our assessment of the agreement between the management and the expert. Our assessment of the same assists in determining the appropriateness of the following:",
          "reference_literature": "(i) The nature, scope and objectives of that expert's work;\n(ii) The respective roles and responsibilities of management and that expert; and\n(iii) The nature, timing and extent of communication between management and that expert, including the form of any report to be provided by that expert."
        },
        {
          "question_id": "B3",
          "question_text": "In case where the expert is employed by the entity, document our inquiries made of the expert and other members of the management and conclusions reached",
          "reference_literature": ""
        }
      ]
    },
    {
      "heading": "C",
      "heading_text": "Evaluate the appropriateness of that expert's work as audit evidence",
      "questions": [
        {
          "question_id": "C1",
          "question_text": "Document our testing of the accuracy and completeness of entity produced data used by the expert and evaluate the relevance and reliability of data from sources external to the entity that are used by the expert",
          "reference_literature": ""
        },
        {
          "question_id": "C2",
          "question_text": "Document our evaluation of whether the significant assumptions used by the expert are reasonable as follows:",
          "reference_literature": "(i) For significant assumptions developed by the expert, take into account the consistency of those assumptions with relevant information.\n(ii) For significant assumptions provided by the entity and used by the expert, look to address: (1) whether they are appropriate (2) whether there are indicators of possible management bias (3) whether they are consistent with each other (4) whether management has the intent to carry out specific courses of action."
        },
        {
          "question_id": "C3",
          "question_text": "If significant assumption is based on the entity's intent and ability to carry out a particular course of action, look to the following requirements:",
          "reference_literature": "(i) The entity's past history of carrying out its stated intentions\n(ii) The entity's written plans or other relevant documentation, such as budgets or minutes\n(iii) The entity's stated reasons for choosing a particular course of action\n(iv) The entity's ability to carry out a particular course of action."
        },
        {
          "question_id": "C4",
          "question_text": "Document our evaluation of whether the methods used by the expert are appropriate under the circumstances, taking into account the requirements of applicable financial reporting framework.",
          "reference_literature": ""
        },
        {
          "question_id": "C5",
          "question_text": "In evaluating the appropriateness of the expert's work as audit evidence for relevant assertion, assess and document:",
          "reference_literature": "(i) Relevance and reasonableness of expert's findings and conclusions, consistency with other audit evidence & whether they have been appropriately reflected in the financial statements\n(ii) Relevance and reasonableness of significant assumptions and methods\n(iii) Relevance, completeness and accuracy of source data."
        },
        {
          "question_id": "C6",
          "question_text": "Evaluate the relevance and reliability of expert's work and whether the expert's findings support or contradict the relevant assertion. Factors that affect the relevance and reliability include:",
          "reference_literature": "(i) the results of our procedures over data, significant assumptions and methods performed above relating to testing of accuracy and completeness of data used by expert and evaluation of significant assumptions used by expert\n(ii) the nature of any restrictions, disclaimers, or limitations in the expert's report or equivalent communication\n(iii) the consistency of expert's work with other audit evidence obtained by us."
        },
        {
          "question_id": "C7",
          "question_text": "If the expert's findings or conclusions appear to contradict the relevant assertion or the expert's work does not provide sufficient appropriate evidence, we shall perform additional procedures, as necessary, to address the matter. Document such procedures performed.",
          "reference_literature": ""
        }
      ]
    }
  ]
};

// Component for clickable reference tooltip
const ReferenceTooltip = ({ content }: { content: string }) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-5 w-5 p-0 text-yellow-400 hover:text-yellow-300 hover:bg-white/10 flex-shrink-0"
          onClick={() => setOpen(!open)}
        >
          <Lightbulb className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        className="max-w-lg p-4 bg-gray-900 text-white border border-gray-700 whitespace-pre-line z-50"
      >
        <p className="text-sm">{content}</p>
      </PopoverContent>
    </Popover>
  );
};

const WORKSPACE_NAME = "Management Use of Expert";

export default function ManagementUseOfExpertAssessment({ onBack }: ManagementUseOfExpertAssessmentProps) {
  const [companyName, setCompanyName] = useState<string>("");
  const [areaPrefix, setAreaPrefix] = useState<string>("MUE"); // Default prefix
  const [actualPhase, setActualPhase] = useState<string>("Execution"); // Default phase
  const [expertColumns, setExpertColumns] = useState<ExpertColumn[]>([
    { id: "expert-1", label: "Expert 1" },
  ]);
  const [questionData, setQuestionData] = useState<Record<string, QuestionData>>({});
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [inferenceQuestion, setInferenceQuestion] = useState<{
    textResponse?: string;
    documents?: any[];
  }>({});
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);
  const [topSaveStatus, setTopSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load area prefix and phase from Sections.json
  useEffect(() => {
    const loadAreaPrefixAndPhase = async () => {
      try {
        if ((window as any).sharePointAPI?.downloadSectionsJson) {
          const result = await (window as any).sharePointAPI.downloadSectionsJson();
          if (result?.success && result?.data) {
            const sectionsData = result.data;
            // Check which phase contains this workspace
            let phase = "Execution"; // Default
            let prefix = "MUE"; // Default
            
            // Check Planning phase
            if (sectionsData.Planning && sectionsData.Planning[WORKSPACE_NAME]) {
              phase = "Planning";
              prefix = sectionsData.Planning[WORKSPACE_NAME];
            }
            // Check Execution phase
            else if (sectionsData.Execution && sectionsData.Execution[WORKSPACE_NAME]) {
              phase = "Execution";
              prefix = sectionsData.Execution[WORKSPACE_NAME];
            }
            // Check ConcludingProcedures phase
            else if (sectionsData.ConcludingProcedures && sectionsData.ConcludingProcedures[WORKSPACE_NAME]) {
              phase = "ConcludingProcedures";
              prefix = sectionsData.ConcludingProcedures[WORKSPACE_NAME];
            }
            
            setActualPhase(phase);
            setAreaPrefix(prefix);
          }
        }
      } catch (error) {
        console.warn("Failed to load area prefix and phase from Sections.json, using defaults:", error);
      }
    };
    loadAreaPrefixAndPhase();
  }, []);

  // Load expert columns from Execution_TailoringQuestions_Responses.json
  const loadExpertColumns = async () => {
    try {
      if ((window as any).sharePointAPI?.downloadClientJson) {
        const result = await (window as any).sharePointAPI.downloadClientJson('Execution_TailoringQuestions_Responses.json');

        if (result?.success && result?.data) {
          const areaData = result.data["Management Use of Expert"];
          
          if (areaData) {
            const savedData = areaData["mue-expert-details"];
            
            if (savedData?.table) {
              const savedTable = savedData.table;
              
              if (savedTable.columns && savedTable.columns.length > 1) {
                // Get expert column names (skip first column which is "Particulars")
                const expertColumnNames = savedTable.columns.slice(1);
                
                // Get the "Name of the expert" row (first row in the table)
                // The rows array contains objects where keys are column names
                const nameRow = savedTable.rows && savedTable.rows.length > 0 ? savedTable.rows[0] : {};
                
                // Create expert columns with names from the "Name of the expert" row
                const expertCols = expertColumnNames.map((colName: string, index: number) => {
                  // Get the expert name from the first row (Name of the expert row)
                  const expertName = nameRow[colName] || colName; // Fallback to column name if not found
                  
                  return {
                    id: `expert-${index + 1}`,
                    label: expertName, // Use the actual expert name as the label
                  };
                });
                
                if (expertCols.length > 0) {
                  setExpertColumns(expertCols);
                  
                  // Initialize question data for all questions and experts
                  const initialData: Record<string, QuestionData> = {};
                  TEMPLATE_DATA.structure.forEach((section: Section) => {
                    section.questions.forEach((question: Question) => {
                      initialData[question.question_id] = {};
                      expertCols.forEach((col: ExpertColumn) => {
                        initialData[question.question_id][col.id] = "";
                      });
                    });
                  });
                  setQuestionData(initialData);
                }
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("Failed to load expert columns:", error);
    }
  };

  // Load saved responses
  const loadResponses = useCallback(async (currentExpertColumns: ExpertColumn[], currentAreaPrefix: string, currentPhase: string) => {
    try {
      if ((window as any).sharePointAPI?.downloadClientJson) {
        // Use the correct file format: <phase name>_<workspace prefix>_Responses.json
        const phaseFormatted = currentPhase === 'ConcludingProcedures' ? 'ConcludingProcedures' 
          : currentPhase.charAt(0).toUpperCase() + currentPhase.slice(1).toLowerCase();
        const fileName = `${phaseFormatted}_${currentAreaPrefix}_Responses.json`;
        const res = await (window as any).sharePointAPI.downloadClientJson(fileName);
        if (res.success && res.data) {
          const substantiveProcedures = res.data.substantiveProcedures || {};
          
          // Load data for each question
          const loadedData: Record<string, QuestionData> = {};
          
          TEMPLATE_DATA.structure.forEach((section: Section) => {
            section.questions.forEach((question: Question) => {
              loadedData[question.question_id] = {};
              
              currentExpertColumns.forEach((col) => {
                const questionResponse = substantiveProcedures[`${question.question_id}_${col.id}`];
                
                if (questionResponse) {
                  // Get text from textResponse.plainText or textResponse.html
                  const text = questionResponse.textResponse?.plainText || 
                              questionResponse.textResponse?.html?.replace(/<[^>]*>/g, '') || 
                              "";
                  loadedData[question.question_id][col.id] = text;
                } else {
                  loadedData[question.question_id][col.id] = "";
                }
              });
            });
          });

          setQuestionData(loadedData);
        }
      }
    } catch (error) {
      console.error("Failed to load responses:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    const initialize = async () => {
      await loadExpertColumns();
    };
    initialize();
  }, []);

  // Load responses when expert columns, area prefix, and phase are loaded
  useEffect(() => {
    if (expertColumns.length > 0 && companyName && areaPrefix && actualPhase) {
      loadResponses(expertColumns, areaPrefix, actualPhase);
    }
  }, [expertColumns, companyName, areaPrefix, actualPhase, loadResponses]);

  // Load inference question data
  useEffect(() => {
    const loadInferenceQuestion = async () => {
      if (!areaPrefix || !actualPhase) return;
      try {
        const phaseFormatted = actualPhase === 'ConcludingProcedures' ? 'ConcludingProcedures' 
          : actualPhase.charAt(0).toUpperCase() + actualPhase.slice(1).toLowerCase();
        const fileName = `${phaseFormatted}_${areaPrefix}_Responses.json`;
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (result?.success && result?.data) {
          const substantiveProcedures = result.data.substantiveProcedures || {};
          const questionResponse = substantiveProcedures['MANAGEMENT-USE-OF-EXPERT-ASSESSMENT-INFERENCE'];
          if (questionResponse) {
            setInferenceQuestion({
              textResponse: questionResponse.textResponse?.plainText || questionResponse.textResponse?.html?.replace(/<[^>]*>/g, '') || '',
              documents: questionResponse.documents || []
            });
          }
        }
      } catch (error) {
        console.error('Failed to load inference question:', error);
      }
    };
    loadInferenceQuestion();
  }, [areaPrefix, actualPhase]);

  // Helper function to sanitize data for JSON serialization
  const sanitizeForJSON = (obj: any): any => {
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    if (typeof obj !== 'object') {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => sanitizeForJSON(item));
    }
    
    // Handle Date objects
    if (obj instanceof Date) {
      return obj.toISOString();
    }
    
    // Create a new object with only serializable properties
    const sanitized: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key];
        // Skip functions, symbols, and other non-serializable types
        if (typeof value === 'function' || typeof value === 'symbol') {
          continue;
        }
        // Recursively sanitize nested objects
        sanitized[key] = sanitizeForJSON(value);
      }
    }
    return sanitized;
  };

  const updateQuestionData = (questionId: string, expertId: string, value: string) => {
    setQuestionData((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [expertId]: value,
      },
    }));
  };

  const handleSave = useCallback(async () => {
    if (!companyName || !areaPrefix) {
      setSaveError("Company name and area prefix are required");
      return;
    }

    setSaving(true);
    setSaveError(null);
    setSaveStatus('saving');

    try {
      // Use the correct file format: <phase name>_<workspace prefix>_Responses.json
      // Format phase name properly (Execution, Planning, or ConcludingProcedures)
      const phaseFormatted = actualPhase === 'ConcludingProcedures' ? 'ConcludingProcedures' 
        : actualPhase.charAt(0).toUpperCase() + actualPhase.slice(1).toLowerCase();
      const fileName = `${phaseFormatted}_${areaPrefix}_Responses.json`;
      
      console.log(`[ManagementUseOfExpertAssessment] Saving to file: ${fileName}`);
      
      // Load existing file first
      let existingData: any = {};
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (result?.success && result.data) {
          existingData = result.data;
        } else if (result?.error) {
          // Handle SharePoint API errors - log but continue with empty data
          console.warn(`Failed to download existing ${fileName}:`, result.error);
        }
      } catch (error: any) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Build payload for all questions and experts
      // Save all cells, even if empty, to maintain data structure
      const payload: Record<string, any> = {};

      TEMPLATE_DATA.structure.forEach((section: Section) => {
        section.questions.forEach((question: Question) => {
          expertColumns.forEach((col) => {
            const cellKey = `${question.question_id}_${col.id}`;
            const cellData = questionData[question.question_id]?.[col.id];
            
            // Always save, even if empty string, to maintain structure
            const text = cellData !== undefined ? cellData : "";
            
            payload[cellKey] = {
              textResponse: {
                html: text,
                plainText: text
              },
              documents: [],
              lastUpdated: new Date().toISOString(),
              companyName: companyName || "",
            };
          });
        });
      });

      console.log(`[ManagementUseOfExpertAssessment] Built payload with ${Object.keys(payload).length} entries`);

      // Merge into existing data
      Object.entries(payload).forEach(([qid, value]) => {
        existingData.substantiveProcedures[qid] = value;
      });

      console.log(`[ManagementUseOfExpertAssessment] After merge, total procedures: ${Object.keys(existingData.substantiveProcedures).length}`);

      // Sanitize the data before stringifying to remove any non-serializable properties
      const sanitizedData = sanitizeForJSON(existingData);
      const jsonString = JSON.stringify(sanitizedData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      // Get FY_YEAR from database dynamically
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      console.log(`[ManagementUseOfExpertAssessment] Saving to ${fileName}`, {
        payloadKeys: Object.keys(payload).length,
        totalProcedures: Object.keys(existingData.substantiveProcedures).length,
        areaPrefix,
        phaseFormatted,
        companyName,
        base64Length: base64Content.length
      });

      if (!(window as any).sharePointAPI?.uploadFile) {
        throw new Error("SharePoint API uploadFile not available");
      }

      console.log(`[ManagementUseOfExpertAssessment] Calling uploadFile with:`, {
        fileName,
        referenceValue: `${phaseFormatted} ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`${phaseFormatted}_${areaPrefix}_Responses`],
      });

      const result = await (window as any).sharePointAPI.uploadFile({
        fileContent: base64Content,
        fileName: fileName,
        referenceValue: `${phaseFormatted} ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`${phaseFormatted}_${areaPrefix}_Responses`],
      });

      console.log(`[ManagementUseOfExpertAssessment] Upload result:`, result);

      if (!result) {
        throw new Error("Upload returned no result");
      }

      if (!result.success) {
        const errorMessage = result.error || "Failed to save responses";
        // Provide more user-friendly error message for common SharePoint errors
        if (errorMessage.includes("Failed to get site ID")) {
          throw new Error("Unable to connect to SharePoint. Please check your connection and try again.");
        }
        console.error(`[ManagementUseOfExpertAssessment] Upload failed:`, errorMessage, result);
        throw new Error(errorMessage);
      }

      console.log(`[ManagementUseOfExpertAssessment] Successfully saved to ${fileName}`);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save responses:", error);
      setSaveError(error instanceof Error ? error.message : "Unknown error");
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  }, [companyName, areaPrefix, actualPhase, expertColumns, questionData]);

  const saveInferenceQuestion = async () => {
    if (!companyName || !areaPrefix || !actualPhase) {
      setInferenceSaveError("Company name, area prefix, and phase are required");
      setInferenceSaveStatus('error');
      return;
    }

    setInferenceSaveStatus('saving');
    setInferenceSaveError(null);

    try {
      // Get text from localStorage if available (DocumentManager auto-saves)
      const itemId = 'MANAGEMENT-USE-OF-EXPERT-ASSESSMENT-INFERENCE';
      const localStorageKey = `autoSaveTextbox_${itemId}`;
      const savedText = localStorage.getItem(localStorageKey) || inferenceQuestion.textResponse || '';

      const phaseFormatted = actualPhase === 'ConcludingProcedures' ? 'ConcludingProcedures' 
        : actualPhase.charAt(0).toUpperCase() + actualPhase.slice(1).toLowerCase();
      const fileName = `${phaseFormatted}_${areaPrefix}_Responses.json`;

      // Download existing file
      let existingData: any = {};
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (result?.success && result.data) {
          existingData = result.data;
        }
      } catch (error) {
        console.warn('Failed to download existing file, starting fresh:', error);
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Wait a bit for localStorage sync
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Query db.json for linked/uploaded documents
      const section = `${phaseFormatted}_${areaPrefix}_MANAGEMENT-USE-OF-EXPERT-ASSESSMENT-INFERENCE`;
      let documents: any[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const docResult = await (window as any).sharePointAPI.loadDocumentListBySection(section);
          if (docResult?.success && Array.isArray(docResult.data)) {
            documents = docResult.data;
          }
        }
      } catch (error) {
        console.warn('Failed to load documents from db.json:', error);
      }

      // Update inference question data
      existingData.substantiveProcedures[itemId] = {
        selectedOption: 'textbox',
        textResponse: {
          html: savedText,
          plainText: savedText
        },
        documents: documents,
        lastUpdated: new Date().toISOString(),
        companyName: companyName
      };

      // Upload updated file
      const sanitizedData = sanitizeForJSON(existingData);
      const jsonString = JSON.stringify(sanitizedData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured");
      }

      const result = await (window as any).sharePointAPI.uploadFile({
        fileContent: base64Content,
        fileName: fileName,
        referenceValue: `${phaseFormatted} ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`${phaseFormatted}_${areaPrefix}_Responses`],
      });

      if (!result?.success) {
        throw new Error(result?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus('saved');
      setTimeout(() => setInferenceSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Failed to save inference question:', error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus('error');
    }
  };

  const getQuestionIdForReview = (): string => {
    return `MANAGEMENT_USE_OF_EXPERT_ASSESSMENT`;
  };

  // Top-right unified save: table responses + inference textbox/documents
  const handleTopSave = useCallback(async () => {
    // Avoid concurrent saves
    if (topSaveStatus === 'saving') return;

    setTopSaveStatus('saving');
    try {
      // Run main responses save first
      await handleSave();
      // Then run inference save
      await saveInferenceQuestion();

      setTopSaveStatus('saved');
      setTimeout(() => setTopSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Top-level save failed in Management Use of Expert Assessment:', error);
      setTopSaveStatus('error');
      setTimeout(() => setTopSaveStatus('idle'), 3000);
    }
  }, [handleSave, saveInferenceQuestion, topSaveStatus]);

  if (loading) {
    return (
      <div className="p-6 text-white space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">Assessment - Management Use of Expert</h2>
            <p className="text-gray-400">Perform detailed procedures over Management Use of Expert</p>
          </div>
          {onBack && (
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
          )}
        </div>
        <Card className="border-white/10 bg-white/5">
          <CardContent className="p-6">
            <div className="text-center text-white/60">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
              <p>Loading...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 text-white space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Assessment - Management Use of Expert</h2>
          <p className="text-gray-400">Perform detailed procedures over Management Use of Expert</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={onBack}
            disabled={!onBack}
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleTopSave}
              className="flex items-center gap-2"
              disabled={topSaveStatus === 'saving' || !companyName || !areaPrefix}
            >
              {topSaveStatus === 'saving' ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save
                </>
              )}
            </Button>
            {topSaveStatus === 'saved' && (
              <span className="text-sm text-green-400">Saved!</span>
            )}
            {topSaveStatus === 'error' && (
              <span className="text-sm text-red-400">Error</span>
            )}
          </div>
        </div>
      </div>

      {saveError && (
        <Card className="border-red-500/20 bg-red-500/10">
          <CardContent className="p-4">
            <p className="text-red-300 text-sm">{saveError}</p>
          </CardContent>
        </Card>
      )}

      {saveStatus === 'saved' && (
        <Card className="border-green-500/20 bg-green-500/10">
          <CardContent className="p-4">
            <p className="text-green-300 text-sm">Responses saved successfully to SharePoint</p>
          </CardContent>
        </Card>
      )}

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Substantive Procedures</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getQuestionIdForReview())}
              className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Review Comment
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {TEMPLATE_DATA.structure.map((section: Section) => (
            <div key={section.heading} className="space-y-4">
              {/* Section Heading */}
              <div className="flex items-center gap-3 pb-2 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white">
                  {section.heading}. {section.heading_text}
                </h3>
              </div>

              {/* Questions Table */}
              <div className="overflow-x-auto rounded-lg border border-white/10">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-white/10 bg-black/20">
                      <th className="border-r border-white/10 p-3 text-left text-sm font-medium text-white w-[300px]">
                        Question
                      </th>
                      {expertColumns.map((column) => (
                        <th
                          key={column.id}
                          className="border-r border-white/10 p-3 text-center text-sm font-medium text-white"
                        >
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {section.questions.map((question: Question) => (
                      <tr key={question.question_id} className="border-b border-white/10">
                        <td className="border-r border-white/10 p-3 text-sm text-white bg-black/20 align-top">
                          <div className="flex items-start gap-2">
                            <div className="flex-1">
                              <div className="flex items-start gap-2">
                                <span className="font-medium">{question.question_id}.</span>
                                <span className="text-xs">{question.question_text}</span>
                                {question.reference_literature && question.reference_literature.trim() && (
                                  <ReferenceTooltip content={question.reference_literature} />
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        {expertColumns.map((column) => {
                          const cellData = questionData[question.question_id]?.[column.id] || "";
                          return (
                            <td key={column.id} className="border-r border-white/10 p-3 align-top">
                              <Textarea
                                value={cellData}
                                onChange={(e) => updateQuestionData(question.question_id, column.id, e.target.value)}
                                className="min-h-[100px] w-full bg-white/5 text-white border-white/10 focus:border-white/30 resize-y"
                                placeholder="Enter response..."
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
            <div className="flex items-center gap-2">
              {inferenceSaveStatus === 'saved' && (
                <span className="text-sm text-green-400">Saved!</span>
              )}
              {inferenceSaveStatus === 'error' && inferenceSaveError && (
                <span className="text-sm text-red-400">Error</span>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey={areaPrefix.toLowerCase()}
            itemId="MANAGEMENT-USE-OF-EXPERT-ASSESSMENT-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) => setInferenceQuestion({ ...inferenceQuestion, textResponse })}
            onDocumentsChange={(documents) => setInferenceQuestion({ ...inferenceQuestion, documents })}
            disabled={false}
          />
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText="Management Use of Expert - Assessment"
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}

